import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import {ObjectId} from 'bson'

import { Button, Animation } from 'components'
import { itemContext } from 'context'
import { Tag } from 'schema'
import { randomTagColor } from 'utils'
import { FaCircle, FaExclamationCircle, FaExclamation } from 'react-icons/fa'

import * as actionCreators from 'store/actions';

const defaultStyle = {
    backgroundColor: 'var(--secondary-color)',
    border: '1px solid var(--primary-accent-color)',
    border: null
}

const Container = styled.div`
    border-radius: 5px;
    padding: 0.1rem;
    background-color: ${props => props.styles.backgroundColor};
    border: ${props => props.styles.border};
    box-shadow: var(--box-shadow);
    width: 15rem;
    display: flex;
    flex-direction: column;
    max-height: 10rem;
    overflow-y: auto;
`

const Title = styled.div`
    font-weight: bolder;
    font-size: 0.7rem;
    padding: 0.5rem 0.5rem 0 0.6rem;
    text-transform: uppercase;
    color: var(--secondary-accent-color);
`

const Placeholder = styled.div`
    font-size: 0.8rem;
    padding: 0.5rem 0.5rem 0.5rem 0.6rem;
    color: var(--secondary-accent-color);
`

const Icon = styled.div`
    color: ${props => props.color || 'var(--secondary-color)'};
    display: flex;
    justify-content: center;
    align-items: center;
`

const Wrapper = styled.div`
    border: ${props => `2px solid ${props.selected ? 'var(--focus-color)' : 'transparent'}`};
    border-radius: 5px;
`

const contextTypes = {
    priority: {
        sign: '!'
    },
    tags: {
        sign: '#'
    }
}

const ContextInput = props => {
    const {
        styles,
        onSelect,
        onClose,
        context,
        type,
        title,
    } = props;

    // Redux
    const dispatch = useDispatch()
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())
    const tags = data['tags']
    const activePageID = useSelector((state) => state.auth.activePageID)
    const user = useSelector((state) => state.auth.user)
    const inputID = useSelector(state => state.content.inputID)

    // Refs
    const containerRef = React.useRef()
    const optionsRef = React.useRef([])
    
    // State
    const updatedStyle = Object.assign({}, defaultStyle, styles)
    const [n, setIndex] = React.useState(-1)

    React.useEffect(() => { // To ensure event listener updates
        const onKeyPress = window.addEventListener('keydown', keyHandler, true)
        return () => {window.removeEventListener('keydown', keyHandler, true)}
    }, [n, tags])

    React.useEffect(() => { // To close when got no text
        context === '' && onClose && onClose()
    }, [context])

    // Create options
    let options = []
    let placeholder
    let newTag
    switch(type){
        case 'priority':
            const text = context.replace('!','')
            let filtered = context ? Object.keys(itemContext.PRIORITY).filter(priority => priority.toLowerCase().includes(text.toLowerCase())) : Object.keys(itemContext.PRIORITY)
            filtered = filtered.length > 0 ? filtered : Object.keys(itemContext.PRIORITY)
            filtered.map((priority,index) => {
                options.push({
                    title: itemContext.PRIORITY_TITLE[priority],
                    value: priority,
                    icon: <Icon color={itemContext.PRIORITY_COLOR[priority.toUpperCase()]}><FaExclamation/></Icon>,
                    styles: {
                        backgroundColor: updatedStyle['backgroundColor'], 
                        boxShadowColor: 'transparent',
                        fontWeight: 'bold',
                        margin: '0.1rem 0',
                        justifyContent: 'flex-start',
                    },
                    onSelect: () => onSelect && onSelect(priority)
                })
            })
            break
        case 'tags':
            const tagText = context.replace('#','').toLowerCase()

            // Create new tag
            if(tagText.length > 0){
                // this.props.createTag(this.state.tag, this.props.data)
                const tagID = new ObjectId().toString();
                newTag = new Tag({ 
                    content: context.replace('#',''),
                    color: randomTagColor(user['theme'])['randomColorCode'],
                    pageID: activePageID,
                    userID: user['uid'],
                    tagID: tagID
                })
                newTag['_id'] = tagID
                options.push({
                    title: `Create ${context.replace('#','')} tag`,
                    onSelect: () => {
                        dispatch(actionCreators.createTag(newTag,data))
                        onSelect && onSelect(newTag['tagID'])
                    },
                    styles: {
                        backgroundColor: updatedStyle['backgroundColor'], 
                        boxShadowColor: 'transparent',
                        // fontWeight: 'bold',
                        justifyContent: 'flex-start',
                        color: 'var(--secondary-accent-color)',
                        margin: '0.1rem 0'
                    }
                })
            }

            // Populate with existing tags
            const pageID = props.pageID || activePageID
            let filteredTags = Object.values(tags).filter(tag => tagText.length > 0 ? tag['content'].toLowerCase().includes(tagText) & tag['pageID'] === pageID : tag['pageID'] === pageID)
            filteredTags.map((tag,index) => {
                options.push({
                    title: tag['content'],
                    value: tag['tagID'],
                    icon: <Icon color={tag['color']}><FaCircle/></Icon>,
                    styles: {
                        backgroundColor: updatedStyle['backgroundColor'], 
                        boxShadowColor: 'transparent',
                        fontWeight: 'bold',
                        margin: '0.1rem 0',
                        justifyContent: 'flex-start',
                    },
                    onSelect: () => onSelect && onSelect(tag['tagID'])
                })
            })

            // Placeholder if no tags
            placeholder = <Placeholder>No tags created</Placeholder>

            
            break
    }

    const keyHandler = (event) => {
        const max = options.length - 2
        let i
        switch(event.keyCode){
            case 40: // Down
                i = n <= max ? n + 1 
                    : n > max ? 0
                    : n
                setIndex(i)
                scrollHandler('down',i)
                break
            case 38: // Up
                i = n > 0 ? n - 1 
                    : n <= 0 ? max + 1
                    : n
                setIndex(i)
                scrollHandler('up',i)
                break
            case 13: // Enter
                onSelect && setTimeout(() => options[n] && options[n]['onSelect'](), 100)
                break
            case 27: // Esc
                // dispatch(actionCreators.setInput(inputID, null))
                break
            case 8: // Backspace
                console.log('[BACKSPACE]')
                context === contextTypes[type]['sign'] && onClose && onClose()
                break
        }
    }

    const scrollHandler = (direction, index) => {
        if(!containerRef.current) { return }

        const containerRect = containerRef.current.getBoundingClientRect()
        let blocks = options
        const bottoms = blocks.map((block, i) => optionsRef.current[i] && optionsRef.current[i].getBoundingClientRect()['bottom'] - containerRect['y'])
        const tops = blocks.map((block, i) => optionsRef.current[i] && optionsRef.current[i].getBoundingClientRect()['top'] - containerRect['y'])

        let scrollTo = -1
        if(index === blocks.length){
            scrollTo = containerRect['height'] + 1000
        } else if (index === 0) {
            scrollTo = 0
        } else {
            scrollTo = bottoms[index] > containerRect['height'] ? tops[index] + containerRef.current.scrollTop
                    // : tops[index] < 0 ? Math.abs(bottoms[index]) + Math.abs(tops[index])
                    : tops[index] < 0 ? containerRef.current.scrollTop + tops[index]
                    : -1
        } 
        scrollTo > -1 && containerRef.current.scrollTo({left: 0, top: scrollTo, behavior: 'smooth'})
    }
    return (
        <Container styles={updatedStyle} ref={containerRef}>     
            {/* {context}   */}
            {title && <Title>{title}</Title>}
            {options.length === 0 && placeholder}
            {options.map((option,index) => (
                <Wrapper selected={n === index}
                    ref={el => optionsRef.current[index] = el}>
                    <Button text={option['title']} 
                        icon={option['icon']}
                        onSelect={option['onSelect']} 
                        styles={option['styles']}/>
                </Wrapper>
            ))}     
        </Container>
    )
}

export default ContextInput